import { useT } from "../../../shared/i18n/i18n";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { Baseline, Check } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../shared/lib/cn";
import { documentHighlightColors, documentTextColors } from "./color-palette";
import { Button } from "@/components/ui/button";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Color controls for the document editor (mnemos color-menu mechanics on
// shadcn): a bubble-toolbar popover for the current selection and a submenu
// for the block menu that colors the whole block.

function SwatchGrid({
  activeHighlight,
  activeText,
  onHighlight,
  onText,
}: {
  activeHighlight: string | null;
  activeText: string | null;
  onHighlight: (value: string | null) => void;
  onText: (value: string | null) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="mb-1 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">{t("colors.text")}</p>
        <div className="flex flex-wrap gap-1">
          <SwatchButton active={activeText === null} label={t("colors.default")} onSelect={() => onText(null)}>
            <span className="text-xs font-semibold">A</span>
          </SwatchButton>
          {documentTextColors.map((color) => (
            <SwatchButton active={activeText === color.value} key={color.id} label={t(color.labelKey)} onSelect={() => onText(color.value)}>
              <span className="text-xs font-semibold" style={{ color: color.value }}>
                A
              </span>
            </SwatchButton>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">{t("colors.highlight")}</p>
        <div className="flex flex-wrap gap-1">
          <SwatchButton active={activeHighlight === null} label={t("colors.none")} onSelect={() => onHighlight(null)}>
            <span className="text-xs">—</span>
          </SwatchButton>
          {documentHighlightColors.map((color) => (
            <SwatchButton active={activeHighlight === color.value} key={color.id} label={t(color.labelKey)} onSelect={() => onHighlight(color.value)}>
              <span className="size-4 rounded-sm" style={{ backgroundColor: color.value }} />
            </SwatchButton>
          ))}
        </div>
      </div>
    </div>
  );
}

function SwatchButton({
  active,
  children,
  label,
  onSelect,
}: {
  active: boolean;
  children: React.ReactNode;
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "relative flex size-6 items-center justify-center rounded-md border border-transparent transition-colors hover:bg-accent",
        active && "border-border bg-accent/60",
      )}
      // Keep the editor selection alive while picking a color.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onSelect}
      title={label}
      type="button"
    >
      {children}
      {active ? <Check className="absolute -right-0.5 -top-0.5 size-2.5 text-primary" /> : null}
    </button>
  );
}

// Bubble-toolbar button: colors the current text selection.
export function SelectionColorButton({ editor }: { editor: Editor }) {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const state = useEditorState({
    editor,
    selector: (context) => ({
      highlight: (context.editor?.getAttributes("highlight").color as string | undefined) ?? null,
      text: (context.editor?.getAttributes("textStyle").color as string | undefined) ?? null,
    }),
  });

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        render={
          <Button
            aria-label={t("colors.textColor")}
            className={cn("size-7 text-muted-foreground [&_svg]:size-3.5", (state?.text || state?.highlight) && "text-foreground")}
            onMouseDown={(event) => event.preventDefault()}
            size="icon-sm"
            title={t("colors.textColor")}
            type="button"
            variant="ghost"
          >
            <Baseline style={state?.text ? { color: state.text } : undefined} />
          </Button>
        }
      />
      <PopoverContent align="start" className="w-60 p-2" side="top">
        <SwatchGrid
          activeHighlight={state?.highlight ?? null}
          activeText={state?.text ?? null}
          onHighlight={(value) => {
            if (value) editor.chain().focus().setHighlight({ color: value }).run();
            else editor.chain().focus().unsetHighlight().run();
          }}
          onText={(value) => {
            if (value) editor.chain().focus().setColor(value).run();
            else editor.chain().focus().unsetColor().run();
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

// Block-menu submenu: colors the whole block's content.
export function BlockColorSubmenu({
  editor,
  getTarget,
}: {
  editor: Editor;
  getTarget: () => { pos: number; size: number } | null;
}) {
  const t = useT();
  function withBlockSelected(action: () => void) {
    const target = getTarget();
    if (!target) return;
    editor
      .chain()
      .focus()
      .setTextSelection({ from: target.pos + 1, to: target.pos + target.size - 1 })
      .run();
    action();
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Baseline data-icon="inline-start" />
        {t("colors.color")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-44">
        <DropdownMenuItem onClick={() => withBlockSelected(() => editor.chain().unsetColor().unsetHighlight().run())}>
          {t("colors.default")}
        </DropdownMenuItem>
        {documentTextColors.map((color) => (
          <DropdownMenuItem
            key={`text-${color.id}`}
            onClick={() => withBlockSelected(() => editor.chain().setColor(color.value).run())}
          >
            <span className="text-sm font-semibold" data-icon="inline-start" style={{ color: color.value }}>
              A
            </span>
            {t(color.labelKey)}
          </DropdownMenuItem>
        ))}
        {documentHighlightColors.map((color) => (
          <DropdownMenuItem
            key={`hl-${color.id}`}
            onClick={() => withBlockSelected(() => editor.chain().setHighlight({ color: color.value }).run())}
          >
            <span className="size-3.5 rounded-sm" data-icon="inline-start" style={{ backgroundColor: color.value }} />
            {t(color.labelKey)} background
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
